import React, { Fragment, useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageTitle from "../layouts/PageTitle";
import { Formik } from "formik";
import { Fn_AddEditData, Fn_DisplayData, Fn_FillListData, Fn_DeleteData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { toast } from "react-toastify";
import useSession from "../utils/useSession";

const AddEdit_AllowanceMaster = () => {
  const { firmId: sessionFirmId } = useSession();
  const defaultFirmId = sessionFirmId ? String(sessionFirmId) : "";

  const [state, setState] = useState({
    id: 0,
    formData: {
      Name: "",
      F_FirmMaster: defaultFirmId,
      Ordering: "",
      IsActive: true,
      IsApplicable: false,
    },
    FirmMaster: [],
    existingAllowances: [],
    isProgress: false,
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  
  const API_URL_SAVE = "AllowanceMaster/0/token";
  const API_URL_EDIT = `${API_WEB_URLS.MASTER}/0/token/AllowanceMaster/Id`;

  // Initial load: firms list & edit data if Id > 0
  useEffect(() => {
    Fn_FillListData(dispatch, setState, "FirmMaster", `${API_WEB_URLS.MASTER}/0/token/FirmMaster/Id/0`);
    const Id = (location.state && location.state.Id) || 0;
    if (Id > 0) {
      setState((prev) => ({ ...prev, id: Id }));
      Fn_DisplayData(dispatch, setState, Id, API_URL_EDIT);
    } else {
      setState((prev) => ({
        ...prev,
        formData: {
          ...prev.formData,
          F_FirmMaster: defaultFirmId,
        },
      }));
    }
  }, [dispatch, location.state, defaultFirmId]);

  // Load existing allowances for the selected firm to detect duplicates and auto-suggest next Ordering
  const activeFirmId = state.formData.F_FirmMaster || defaultFirmId;
  useEffect(() => {
    if (activeFirmId) {
      Fn_FillListData(
        dispatch,
        setState,
        "existingAllowances",
        `${API_WEB_URLS.MASTER}/0/token/AllowanceMaster/TBL.F_FirmMaster/${activeFirmId}`
      );
    } else {
      setState((prev) => ({ ...prev, existingAllowances: [] }));
    }
  }, [dispatch, activeFirmId]);
  
  // Compute next available ordering for this firm
  const nextAvailableOrdering = useMemo(() => {
    if (!state.existingAllowances || state.existingAllowances.length === 0) return 1;
    const maxOrder = Math.max(
      0,
      ...state.existingAllowances.map((a) => Number(a.Ordering) || 0)
    );
    return maxOrder + 1;
  }, [state.existingAllowances]);

  // When creating a new allowance and existing allowances load, prefill Ordering if empty
  useEffect(() => {
    if (state.id === 0 && (!state.formData.Ordering || state.formData.Ordering === "")) {
      setState((prev) => ({
        ...prev,
        formData: {
          ...prev.formData,
          Ordering: nextAvailableOrdering,
        },
      }));
    }
  }, [state.id, nextAvailableOrdering, state.formData.Ordering]);

  // Comprehensive Validation: Required checks + Duplicate Ordering & Name check for same firm
  const validate = (values) => {
    const errors = {};

    if (!values.Name || !values.Name.trim()) {
      errors.Name = "Allowance name is required";
    }

    const firmId = values.F_FirmMaster || defaultFirmId;
    if (!firmId) {
      errors.F_FirmMaster = "Firm is required";
    }

    if (values.Ordering === "" || values.Ordering === undefined || values.Ordering === null) {
      errors.Ordering = "Ordering is required";
    } else if (Number(values.Ordering) <= 0) {
      errors.Ordering = "Ordering must be greater than 0";
    } else if (state.existingAllowances && state.existingAllowances.length > 0) {
      const currentId = Number(state.id) || 0;
      const targetOrder = Number(values.Ordering);

      const duplicateOrder = state.existingAllowances.find(
        (item) => Number(item.Ordering) === targetOrder && Number(item.Id) !== currentId
      );

      if (duplicateOrder) {
        errors.Ordering = `Ordering ${targetOrder} is already assigned to "${duplicateOrder.Name}" in this firm.`;
      }

      if (values.Name && values.Name.trim()) {
        const trimmedName = values.Name.trim().toLowerCase();
        const duplicateName = state.existingAllowances.find(
          (item) =>
            item.Name &&
            item.Name.trim().toLowerCase() === trimmedName &&
            Number(item.Id) !== currentId
        );
        if (duplicateName) {
          errors.Name = `Allowance "${values.Name.trim()}" already exists for this firm.`;
        }
      }
    }

    return errors;
  };

  const handleSubmit = async (values) => {
    try {
      const formData = new FormData();
      formData.append("Name", values.Name.trim());
      formData.append("F_FirmMaster", values.F_FirmMaster || defaultFirmId || 0);
      formData.append("Ordering", values.Ordering);
      formData.append("IsActive", values.IsActive ? true : false);
      formData.append("IsApplicable", values.IsApplicable ? true : false);

      Fn_AddEditData(
        dispatch,
        setState,
        { arguList: { id: state.id, formData } },
        API_URL_SAVE,
        true,
        "memberid",
        navigate,
        "/AllowanceMaster"
      );
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An error occurred while submitting the form. Please try again.");
    }
  };

  return (
    <Fragment>
      <PageTitle activeMenu="Masters" motherMenu="Masters" pageContent="Allowance Master" />

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Allowance Master</h4>
            </div>
            <div className="card-body">
              <div className="basic-form">
                <Formik
                  initialValues={state.formData}
                  enableReinitialize
                  validate={validate}
                  onSubmit={(values, { setSubmitting }) => {
                    setTimeout(() => {
                      handleSubmit(values);
                      setSubmitting(false);
                    }, 200);
                  }}
                >
                  {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => (
                    <form onSubmit={handleSubmit}>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="text-label">Allowance Name *</label>
                          <input
                            type="text"
                            className={`form-control ${errors.Name && (touched.Name || values.Name) ? "is-invalid" : ""}`}
                            name="Name"
                            placeholder="e.g. Basic, DA, HRA"
                            value={values.Name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.Name && (touched.Name || values.Name) && (
                            <div className="text-danger fs-12 mt-1">{errors.Name}</div>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="text-label">
                            Firm * {defaultFirmId ? <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "11px" }}>🔒 Header Locked</span> : null}
                          </label>
                          <select
                            className={`form-control ${errors.F_FirmMaster && touched.F_FirmMaster ? "is-invalid" : ""}`}
                            name="F_FirmMaster"
                            value={values.F_FirmMaster || defaultFirmId}
                            disabled={!!defaultFirmId}
                            style={defaultFirmId ? { backgroundColor: "#f8f9fa", cursor: "not-allowed" } : {}}
                            onChange={(e) => {
                              handleChange(e);
                              setState((prev) => ({
                                ...prev,
                                formData: { ...prev.formData, F_FirmMaster: e.target.value },
                              }));
                            }}
                            onBlur={handleBlur}
                          >
                            <option value="">-- Select Firm --</option>
                            {state.FirmMaster
                              .filter((f) => !f.IsNotInUse || String(f.Id) === String(values.F_FirmMaster || defaultFirmId))
                              .map((f) => <option key={f.Id} value={f.Id}>{f.Name}</option>)}
                          </select>
                          {errors.F_FirmMaster && touched.F_FirmMaster && (
                            <div className="text-danger fs-12 mt-1">{errors.F_FirmMaster}</div>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <label className="text-label mb-0">Ordering *</label>
                            {state.id === 0 && nextAvailableOrdering && (
                              <span className="text-muted" style={{ fontSize: "11px" }}>
                                Next available: <strong className="text-primary">{nextAvailableOrdering}</strong>
                              </span>
                            )}
                          </div>
                          <input
                            type="number"
                            min="1"
                            className={`form-control ${errors.Ordering && (touched.Ordering || values.Ordering) ? "is-invalid" : ""}`}
                            name="Ordering"
                            value={values.Ordering}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.Ordering && (touched.Ordering || values.Ordering) ? (
                            <div className="text-danger fs-12 mt-1">{errors.Ordering}</div>
                          ) : (
                            <small className="text-muted" style={{ fontSize: "11px" }}>
                              Controls the display and column sequence of this allowance on payslips, reports, and payroll entry. Must be unique per firm.
                            </small>
                          )}
                        </div>

                        <div className="col-md-6 mb-3 d-flex align-items-center mt-4">
                          <div className="form-check me-4">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="IsActive"
                              name="IsActive"
                              checked={!!values.IsActive}
                              onChange={(e) => setFieldValue("IsActive", e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="IsActive">
                              Is Active
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="IsApplicable"
                              name="IsApplicable"
                              checked={!!values.IsApplicable}
                              onChange={(e) => setFieldValue("IsApplicable", e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="IsApplicable">
                              Is Applicable
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3">
                        <button type="submit" className="btn btn-primary me-2" disabled={isSubmitting || !!errors.Ordering || !!errors.Name}>
                          {isSubmitting ? "Saving..." : "Save"}
                        </button>
                        {state.id > 0 && (
                          <button
                            type="button"
                            className="btn btn-danger me-2"
                            onClick={async () => {
                              if (window.confirm(`Are you sure you want to delete allowance "${values.Name || state.id}"?`)) {
                                try {
                                  await Fn_DeleteData(dispatch, () => {}, Number(state.id), `${API_WEB_URLS.MASTER}/0/token/AllowanceMaster`);
                                  toast.success("Allowance deleted successfully");
                                  navigate("/AllowanceMaster");
                                } catch (err) {
                                  console.error("Error deleting allowance:", err);
                                }
                              }
                            }}
                          >
                            Delete
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-danger light"
                          onClick={() => navigate("/AllowanceMaster")}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddEdit_AllowanceMaster;
